import { FormHelperText } from "@mui/material";
import { useMprcTheme } from "components/MprcThemeProvider";
import { Delta, EmitterSource, MprcQuillContainer, Quill, QuillCommands, QuillEditorProps, QuillTranslateToolbar } from "components/Quill";
import * as React from "react";
import { useImperativeHandle } from "react";

///////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

const MprcQuillEditorComponent: React.ForwardRefRenderFunction<QuillCommands, QuillEditorProps> = (props, forwardRef) => {

    const {
        children, initialValue, error, helperText, placeholder, formats, modules,
        onChange, onQuillReady, onRegisterModules, onUnmount, onFocus, onBlur,
        theme = "snow",
        autoFocus = false,
        disabled = false,
        disableInitialValueMonitoring = false,
    } = props;
    const { theme: mprcTheme } = useMprcTheme();

    const id = React.useMemo(() => { return props.id || `ed_${new Date().getTime()}`; }, [props.id]);
    const iv = React.useRef<string | null | undefined>(initialValue);

    const style: React.CSSProperties = {
        border: props.error ? "solid 1px red" : undefined,
        minHeight: props.minHeight || "auto",
        maxHeight: props.maxHeight || "auto",
        overflowY: "hidden",
        marginBottom: props.marginBottom ? props.marginBottom : undefined,
    };

    //////////////////////////////////////////////
    const containerRef = React.useRef<HTMLDivElement>(null);
    const quill = React.useRef<Quill>(null!);

    //////////////////////////////////////////////

    const focus = () => { quill.current?.focus(); };
    const disable = () => { quill.current?.disable(); };
    const clear = () => { if (quill.current!) quill.current!.root.innerHTML = ""; };
    const setValue = (value: string) => { if (quill.current!) quill.current!.root.innerHTML = value; };
    const getValueHtml = () => quill.current!.getSemanticHTML(); //root.innerHTML ?? "";
    const getValueText = () => quill.current!.root.innerText == "\n" ? "" : (quill.current!.root.innerText ?? "");
    const insert = (blotName: string, data?: any) => {
        const range = quill.current.getSelection(true);
        if (range != null) {
            quill.current.insertEmbed(range.index, blotName, data, "api");
            quill.current.insertText(range.index + 1, ' ', "api");
            quill.current.setSelection(range.index + 2, 1, "silent");
        }
    };
    const getQuillRef = () => quill.current!;

    useImperativeHandle(forwardRef, () => ({
        focus,
        disable,
        insert,
        clear,
        setValue,
        getQuillRef,
        getValueHtml,
        getValueText
    }));

    //////////////////////////////////////////////

    const quillOnChangeHandle = React.useCallback((delta: Delta, oldDelta: Delta, source: EmitterSource) => {
        if (quill.current && quill.current.getSemanticHTML() != iv.current) {
            onChange?.(
                quill.current.root.innerText,
                delta,
                source,
                {
                    getBounds: quill.current.getBounds,
                    getLength: quill.current.getLength,
                    getText: (index?: number, length?: number) => quill.current.getText(index, length),
                    getHTML: () => quill.current.getSemanticHTML(),
                    getSelection: quill.current.getSelection,
                    getContents: quill.current.getContents
                });
        }
    }, [quill, iv.current]);

    const monitoringTextChange = (active: boolean) => {
        if (quill) {
            if (active) quill.current.on('text-change', quillOnChangeHandle);
            else quill.current.off('text-change', quillOnChangeHandle);
        }
    };

    const focusEnter = () => quill.current && onFocus?.(id, quill.current.root.innerHTML);
    const focusBlur = () => quill.current && onBlur?.(id, quill.current.root.innerHTML);

    React.useEffect(() => {
        if (!containerRef.current || quill.current) return;

        onRegisterModules?.(Quill);

        quill.current = new Quill(containerRef.current, { theme, modules, formats, placeholder });

        onQuillReady && onQuillReady();

        var qtr = new QuillTranslateToolbar(id, mprcTheme);
        qtr.translate();

        if (initialValue) quill.current.clipboard.dangerouslyPasteHTML(0, initialValue, "silent");
        if (autoFocus) quill.current.focus();
        if (disabled) quill.current.disable();
        monitoringTextChange(true);

        var elementChild = document.getElementById(id)?.firstChild;
        elementChild?.addEventListener("focus", focusEnter);
        elementChild?.addEventListener("blur", focusBlur);

        return () => {
            elementChild?.removeEventListener("focus", focusEnter);
            elementChild?.removeEventListener("blur", focusBlur);
            onUnmount && onUnmount();
            monitoringTextChange(false);
        };
    }, []);

    React.useEffect(() => {
        if (quill && iv.current !== initialValue && !disableInitialValueMonitoring) {
            // Solução adaptada de 'https://github.com/quill-mention/quill-mention/issues/174' para remover
            // o problema de duplicação da tag "<span contenteditable='false>" quando repopulando
            // um conteúdo que contenha menções.
            monitoringTextChange(false);
            quill.current.root.innerHTML = "";
            quill.current.clipboard.dangerouslyPasteHTML(0, initialValue ?? "", "silent");
            iv.current = initialValue;
            monitoringTextChange(true);
        }
    }, [initialValue, disableInitialValueMonitoring]);

    return (
        <>
            {children}
            <MprcQuillContainer spellCheck={false} style={style} showToolbar={props.toolbar ?? true} >
                <div id={id} ref={containerRef} />
                {error && helperText && <FormHelperText error>{helperText}</FormHelperText>}
            </MprcQuillContainer>
        </>
    );
};

export const MprcQuillEditor = React.forwardRef(MprcQuillEditorComponent);
